import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { createAndSendInvoice,
  createRecurringSubscription,
  listStripePrices,
} from '../../services/stripeBillingApi';
import { listStripeCustomersResolved } from '../../services/stripeResolved';
import { useStripeApi } from '../../hooks/useStripeApi';

function fmtMoney(cents: number, cur: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur.toUpperCase() }).format(cents / 100);
}

type Props = {
  onSuccess?: () => void;
  onMessage?: (msg: { type: 'success' | 'error'; text: string }) => void;
};

export function StripeBillingActions({ onSuccess, onMessage }: Props) {
  const { settings, isSecretConfigured, ready } = useStripeApi();
  const billing = settings.integrations.stripe.billing;
  const [createOpen, setCreateOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [customers, setCustomers] = useState<Array<{ id: string; label: string }>>([]);
  const [prices, setPrices] = useState<
    Array<{ id: string; nickname: string; amount: number; currency: string; interval: string }>
  >([]);
  const [form, setForm] = useState({ customerId: '', amount: '', description: 'Monthly service', priceId: '' });

  const loadOptions = useCallback(async () => {
    if (!ready) {
      return;
    }
    const [cust, pr] = await Promise.all([
      listStripeCustomersResolved(settings, isSecretConfigured, { limit: 50 }).then((r) =>
        r.data.map((c) => ({
          id: c.id,
          label: [c.name, c.email].filter(Boolean).join(' · ') || c.id,
        }))
      ),
      listStripePrices(settings),
    ]);
    setCustomers(cust);
    setPrices(pr);
    if (cust.length && !form.customerId) {
      setForm((f) => ({ ...f, customerId: cust[0].id }));
    }
  }, [ready, settings, isSecretConfigured, form.customerId]);

  useEffect(() => {
    if (createOpen || subOpen) {
      void loadOptions();
    }
  }, [createOpen, subOpen, loadOptions]);

  if (!ready) {
    return null;
  }

  return (
    <>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New invoice
        </Button>
        <Button variant="outlined" startIcon={<AutorenewOutlinedIcon />} onClick={() => setSubOpen(true)}>
          Recurring subscription
        </Button>
      </Stack>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create & send invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Customer"
              fullWidth
              value={form.customerId}
              onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount"
              type="number"
              fullWidth
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              helperText={`Currency: ${billing.defaultCurrency.toUpperCase()}`}
            />
            <TextField
              label="Description"
              fullWidth
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              void (async () => {
                try {
                  const cents = Math.round(parseFloat(form.amount) * 100);
                  await createAndSendInvoice(settings, {
                    customerId: form.customerId,
                    amountCents: cents,
                    description: form.description,
                  });
                  setCreateOpen(false);
                  onMessage?.({ type: 'success', text: 'Invoice created and sent' });
                  onSuccess?.();
                } catch (e) {
                  onMessage?.({ type: 'error', text: e instanceof Error ? e.message : 'Failed' });
                }
              })();
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={subOpen} onClose={() => setSubOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Recurring subscription</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Customer"
              fullWidth
              value={form.customerId}
              onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Price (from Stripe)"
              fullWidth
              value={form.priceId}
              onChange={(e) => setForm((f) => ({ ...f, priceId: e.target.value }))}
            >
              {prices.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nickname} — {fmtMoney(p.amount, p.currency)}/{p.interval}
                </MenuItem>
              ))}
            </TextField>
            {prices.length === 0 && (
              <Alert severity="info">Create recurring Prices in Stripe Dashboard first.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.customerId || !form.priceId}
            onClick={() => {
              void (async () => {
                try {
                  await createRecurringSubscription(settings, form.customerId, form.priceId);
                  setSubOpen(false);
                  onMessage?.({ type: 'success', text: 'Subscription created — Stripe will invoice each period' });
                  onSuccess?.();
                } catch (e) {
                  onMessage?.({ type: 'error', text: e instanceof Error ? e.message : 'Failed' });
                }
              })();
            }}
          >
            Start subscription
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
